import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, FolderOpenIcon, LineChartIcon, LockIcon, TableIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { ScenarioKeyTable } from '../../components/demo/ScenarioKeyTable';
import { LedgerTable } from '../../components/workspace/LedgerTable';
import { BudgetTable } from '../../components/workspace/BudgetTable';
import { DocumentViewer } from '../../components/workspace/DocumentViewer';
import { ASSESSMENT_URL, PACKAGE_HASH } from '../../data/demo';
import { TabId } from '../../types/assessment';
type PreviewTab = TabId | 'key';
const TABS: {
  id: PreviewTab;
  label: string;
  icon: typeof TableIcon;
}[] = [{
  id: 'reconciliation',
  label: 'Reconciliation',
  icon: TableIcon
}, {
  id: 'budget',
  label: 'P&L vs Budget',
  icon: LineChartIcon
}, {
  id: 'documents',
  label: 'Source documents',
  icon: FolderOpenIcon
}, {
  id: 'key',
  label: 'Assessment key',
  icon: LockIcon
}];
export function InstrumentPreview() {
  const {
    mode,
    slots
  } = useDemo();
  const [tab, setTab] = useState<PreviewTab>('reconciliation');
  const navigate = useNavigate();
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.25,
    ease: [0.23, 1, 0.32, 1]
  }} className="flex min-h-full w-full flex-col bg-paper" data-id="element-580" data-mp="vtl6tup0icw4">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-581" data-mp="1ohh16sr3zspq">
        <LenticMark data-id="element-582" data-mp="1jinm4vw6btng" />
        <BackLink to="/demo/pipeline" label="Back to the gates" data-id="element-583" data-mp="1ji4ec808mln5" />
      </div>

      <main className="mx-auto w-full max-w-[1500px] flex-1 px-8 py-8" data-id="element-584" data-mp="1ejjfod0mwa0t">
        <div className="flex flex-wrap items-center gap-3" data-id="element-585" data-mp="1ejb2sl4ih4pm">
          <h1 className="text-[20px] font-semibold tracking-tight text-ink" data-id="element-586" data-mp="83qt7awvckat">
            FIN-001 v0.1 · Meridian SAS · Q2 2026 close
          </h1>
          <span className="rounded-sm border border-rule bg-white px-2.5 py-1 font-mono-num text-[11px] text-muted" data-id="element-587" data-mp="36gut5ri4yau">
            {PACKAGE_HASH}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-brand px-2.5 py-1 font-mono-num text-[11px] uppercase tracking-wider text-brand" data-id="element-588" data-mp="9jcsd0evq7s8">
            <LockIcon className="h-3 w-3" data-id="element-589" data-mp="jpswk5pnc1t" />
            Locked
          </span>
        </div>

        <p className="mt-2 text-[13.5px] text-muted" data-id="element-590" data-mp="3i0pqpaaim2d6">
          {mode === 'single' ? 'Tailored probe — comparability is moot by design.' : 'This is the candidate’s workspace — assistant included, sources withheld from it.'}
        </p>

        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_300px]" data-id="element-591" data-mp="391m4hxg1x6ug">
          <div className="overflow-hidden rounded-sm border border-rule bg-white" data-id="element-592" data-mp="3d264d8s0eoka">
            <div className="flex items-center gap-1 border-b border-rule bg-paper px-3 pt-2.5" data-id="element-593" data-mp="2z1buelpbcfji">
              {TABS.map(t => {
              const active = t.id === tab;
              return <button key={t.id} type="button" onClick={() => setTab(t.id)} aria-current={active ? 'page' : undefined} className={`flex items-center gap-2 rounded-t-sm border border-b-0 px-4 py-2 text-[13px] font-medium transition-colors duration-150 ease-settle ${active ? 'border-rule bg-white text-ink' : 'border-transparent text-muted hover:text-ink'}`} data-id="element-594" data-mp="2kfxxwhgon636">
                    <t.icon className="h-3.5 w-3.5" data-id="element-595" data-mp="3d3ai4kzdf1tb" />
                    {t.label}
                  </button>;
            })}
            </div>
            <div className="h-[560px] overflow-hidden" data-id="element-596" data-mp="3858z201y32hu">
              {tab === 'reconciliation' && <LedgerTable data-id="element-597" data-mp="3m4rvhvle8peb" />}
              {tab === 'budget' && <BudgetTable data-id="element-598" data-mp="3i2wuytbaeg1l" />}
              {tab === 'documents' && <DocumentViewer data-id="element-599" data-mp="3i4hteu0q2j4q" />}
              {tab === 'key' && <div className="h-full overflow-y-auto px-5 py-5" data-id="element-600" data-mp="1feyecxtlhwqp">
                  <p className="text-[13px] text-muted" data-id="element-601" data-mp="1ket3avtt8c94">
                    The buyer’s key. Every line the reviewer will need to read the record.
                  </p>
                  <div className="mt-5" data-id="element-602" data-mp="1p7vzzctvw1tj">
                    <ScenarioKeyTable showCorrectAction data-id="element-603" data-mp="1u5dysae6t0nt" />
                  </div>
                </div>}
            </div>
          </div>

          <aside data-id="element-604" data-mp="wiygx7hum0zq">
            <p className="eyebrow text-muted" data-id="element-605" data-mp="wjgnoy24zxlw">Scenario slots · gate results</p>
            <ul className="mt-4 space-y-2" data-id="element-606" data-mp="11ggtd7v2qych">
              {slots.map(slot => <li key={slot.id} className={`rounded-sm border border-rule bg-white p-3.5 ${slot.klass === 'CLEAN' ? 'border-l-2 border-l-brand' : ''}`} data-id="element-607" data-mp="16dqg2fr2wvwm">
                  <div className="flex items-baseline justify-between gap-2" data-id="element-608" data-mp="2lvs6tpzirirk">
                    <span className="font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-brand" data-id="element-609" data-mp="2lxwkv0r4ty5k">
                      {slot.klass}
                    </span>
                    <span className="font-mono-num text-[10.5px] text-muted" data-id="element-610" data-mp="67mzuseohwck">{slot.row}</span>
                  </div>
                  <p className="mt-1.5 text-[12.5px] leading-snug text-ink" data-id="element-611" data-mp="1ek0g3n7zsog">{slot.title}</p>
                  <dl className="mt-2.5 space-y-1 font-mono-num text-[10.5px] text-muted" data-id="element-612" data-mp="3smfsjffc5u8w">
                    <div className="flex gap-2" data-id="element-613" data-mp="3np65u7jbzwor">
                      <dt className="w-[68px] shrink-0" data-id="element-614" data-mp="3iorvgiu9cank">consistency</dt>
                      <dd className="text-ink" data-id="element-615" data-mp="3iqmgry9fy4s6">{slot.consistency}</dd>
                    </div>
                    <div className="flex gap-2" data-id="element-616" data-mp="3ep10s41m1adr">
                      <dt className="w-[68px] shrink-0" data-id="element-617" data-mp="39olozgwz8axw">solvability</dt>
                      <dd className="leading-snug" data-id="element-618" data-mp="1cvjxfckiivot">{slot.solvability}</dd>
                    </div>
                  </dl>
                </li>)}
            </ul>
          </aside>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-619" data-mp="1r1c2iacyew0a">
          <button type="button" onClick={() => navigate('/demo/invite')} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-620" data-mp="1qyzaf4dhf59i">
            Invite candidates
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-621" data-mp="2apch0qajvsnj" />
          </button>
          <button type="button" onClick={() => navigate('/demo/record')} className="text-[13px] text-muted underline decoration-rule underline-offset-4 transition-colors hover:text-ink" data-id="element-622" data-mp="2epd7xaeu5wbh">
            Skip ahead — see what comes back
          </button>
          <a href={ASSESSMENT_URL} target="_blank" rel="noreferrer" className="text-[13px] text-brand underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-623" data-mp="payn316xutl9">
            Open the live assessment ↗
          </a>
        </div>
      </main>

      <DemoFooter data-id="element-624" data-mp="vrqbgejehm7g" />
    </motion.div>;
}